<template>
  <div class="panel" :style="{ height: panelHeight + 'px' }">
    <div
      class="panel__handle"
      @touchstart.stop="onTouchStart"
      @touchmove.stop.prevent="onTouchMove"
      @touchend.stop="onTouchEnd"
      @click.stop="onHandleTap"
    >
      <div class="panel__handle__bar"></div>
      <div class="panel__handle__title">
        {{
          $t("app.home.map_store_query_title") +
          (!innerLocationPermission
            ? $t("app.home.map_store_location_fallback_hint")
            : "")
        }}
      </div>
    </div>

    <div class="panel__filters">
      <div class="panel__filters__city" @click="openCityPopup">
        <div class="panel__cityTrigger" role="button">
          <span class="panel__cityTriggerText">{{ currentCityLabel }}</span>
          <img
            class="panel__cityArrow"
            :src="showCityPopup ? iconMapUp : iconMapDown"
            alt=""
          />
        </div>
      </div>
      <van-popup
        v-model:show="showCityPopup"
        position="bottom"
        round
        :style="{ height: '50%' }"
        @open="onCityPopupOpen"
      >
        <div class="panel__cityPopup">
          <div class="panel__cityPopupTitle">
            {{ $t("app.home.map_store_select_province") }}
          </div>
          <div class="panel__citySearchWrap">
            <van-field
              v-model.trim="citySearchKeyword"
              :placeholder="
                $t('app.home.map_store_search_province_placeholder')
              "
              class="panel__citySearch"
            />
            <van-icon
              v-show="hasCitySearchKeyword"
              name="cross"
              class="panel__citySearchClear"
              @click="citySearchKeyword = ''"
            />
          </div>
          <div class="panel__cityPopupList">
            <div
              v-for="opt in filteredCityOptions"
              :key="String(opt.value)"
              class="panel__cityOption"
              :class="{ 'panel__cityOption--active': opt.value === innerCity }"
              @click="selectCityMobile(opt)"
            >
              {{ opt.text }}
            </div>
          </div>
        </div>
      </van-popup>

      <van-field
        class="panel__filters__search"
        v-model.trim="innerKeyword"
        clearable
        clear-trigger="always"
        :placeholder="$t('app.home.map_store_search_store_placeholder')"
        @update:model-value="emitKeyword"
        @clear="onClearKeyword"
      >
        <template #left-icon>
          <span class="panel__searchIconWrap">
            <img class="panel__searchIcon" :src="iconSearch" alt="" />
          </span>
        </template>
      </van-field>
    </div>

    <!-- 无搜索结果：面板收起到空状态高度，展示配图 + 文案（设计稿） -->
    <div v-if="list.length === 0" class="panel__empty">
      <p class="panel__empty__line1">
        {{ $t("app.home.map_store_empty_line1") }}
      </p>
      <p class="panel__empty__line2">
        {{ $t("app.home.map_store_empty_line2") }}
      </p>
    </div>
    <div v-else class="panel__list" @touchmove.stop>
      <div
        v-for="(item, idx) in list"
        :key="item.id ?? idx"
        class="store"
        :class="{ 'store--active': (item.id ?? idx) === activeKey }"
        @click="onSelect(item, idx)"
      >
        <div class="store__head">
          <div class="store__name">{{ item.fullName || "-" }}</div>
          <div class="store__right" v-if="item.showdistancekm">
            <img class="store__navicon" :src="iconNav" alt="" />
            <div class="store__distance">
              {{ item.distancekm + "km" }}
            </div>
          </div>
        </div>

        <!-- <div class="store__badge" v-if="formatAuthRange(item)">
          授权日期：{{ formatAuthRange(item) }}
        </div> -->

        <div class="store__main">
          <div class="store__rows">
            <div class="store__row" v-if="item.phoneNumber || item.phone">
              <img class="store__row__imgicon" :src="iconPhone" alt="" />
              <div class="store__row__text">
                {{ item.phoneNumber || item.phone }}
              </div>
            </div>

            <div class="store__row" v-if="item.fullAddress">
              <img class="store__row__location" :src="iconLoc" alt="" />
              <div class="store__row__text">{{ item.fullAddress }}</div>
            </div>
          </div>

          <button
            v-if="item.distancekm && item.showdistancekm"
            class="store__navbtn"
            @click.stop="onNavigateWithSelect(item, idx)"
          >
            {{ $t("app.home.map_store_nav_button") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, nextTick, onMounted, reactive } from "vue";
import { useI18n } from "vue-i18n";
import iconPhone from "@/assets/map-icons/phone.png";
import iconNav from "@/assets/map-icons/navigation.png";
import iconLoc from "@/assets/map-icons/map_location.png";
import iconSearch from "@/assets/map-icons/map_search.png";
import iconMapDown from "@/assets/map-icons/map_down.png";
import iconMapUp from "@/assets/map-icons/map_up.png";
const THEME = "#009343";
import {
  CHINA_ALL_CITY_LIST,
  CHINA_ALL_PROVINCE,
  CHINA_DEFAULT_PROVINCE_ID,
  getAllProvinceList,
} from "@/utils/config";
import { isInChina } from "@/utils/commonUtils";
import {
  getCityListApi,
  getOpenRetailerListApi,
  getMultiLevelDistrictApi,
} from "@/utils/neturls";
import { debounce } from "@/utils/commonUtils";
import { calcDistanceKm } from "@/utils/geoCity";

const useCitySourceEffect = (
  innerCity,
  innerKeyword,
  innerCurrentLocation,
  list
) => {
  const getAllDistrictList = async () => {
    // 当前所有的
    let param = {
      provinceIdEqual: [innerCity.value],
    };
    const query = [];
    Object.keys(param).forEach((key) => {
      let list = param[key];
      let innerquery;
      if (list && Array.isArray(list)) {
        innerquery = list
          .filter((item) => item)
          .map((item) => `${key}=${item}`)
          .join("&");
        if (innerquery) {
          query.push(innerquery);
        }
      }
    });
    const finalquery = query.join("&"); // 手动拼接查询字符串
    console.log("getAllDietrictList finalquery", finalquery);
    const res = await getMultiLevelDistrictApi(finalquery, null);
    if (res.error == null) {
      return res.data.list || [];
    }
    return [];
  };

  const getStoreList = debounce(async () => {
    const allDistrictList = await getAllDistrictList();
    if (allDistrictList || allDistrictList.length > 0) {
      let districtIdList = allDistrictList.map((item) => item.id);
      // 处理一下数据
      let param = {
        districtIdEqual: [districtIdList],
        fullNameLike: [innerKeyword.value],
      };
      const query = [];
      Object.keys(param).forEach((key) => {
        let list = param[key];
        let innerquery;
        if (list && Array.isArray(list)) {
          innerquery = list
            .filter((item) => item)
            .map((item) => `${key}=${item}`)
            .join("&");
          if (innerquery) {
            query.push(innerquery);
          }
        }
      });
      const finalquery = query.join("&"); // 手动拼接查询字符串
      console.log("finalquery", finalquery);
      // 这个是老的获取经销商的列表  先暂时隐藏掉吧
      // const res = await getDealerListApi(param);
      const res = await getOpenRetailerListApi(finalquery, null);
      if (res.error == null) {
        // let dealerList = res.data.list;
        list.value = res.data.list || [];

        for (let index = 0; index < list.value.length; index++) {
          const item = list.value[index];
          //伪造一些数据
          // if (isInChina()) {
          //   if (item.address == "解放东路225号") {
          //     item.latitude = "31.8944639";
          //     item.longitude = "121.2010698";
          //   } else if (item.address == "丹阳路") {
          //     item.latitude = "32.204402";
          //     item.longitude = "119.452753";
          //   } else {
          //     if (index == 0) {
          //       item.latitude = "31.2185";
          //       item.longitude = "121.5512";
          //     }
          //     if (index == 1) {
          //       item.latitude = "31.2310";
          //       item.longitude = "121.4770";
          //     }
          //     if (index == 2) {
          //       item.latitude = "31.1732";
          //       item.longitude = "121.3985";
          //     }
          //     if (index == 3) {
          //       item.latitude = "31.2721";
          //       item.longitude = "121.4503";
          //     }
          //   }
          // } else {
          //   if (item.address == "oneone floor") {
          //     item.latitude = "3.1578";
          //     item.longitude = "101.7119";
          //   }
          //   if (item.address === "twotwo floor") {
          //     item.latitude = "1.4576";
          //     item.longitude = "103.7611";
          //   }
          // }

          item.fullAddress =
            item?.district?.city?.province?.localizedFullName +
            item?.district?.city?.localizedFullName +
            item?.district?.localizedFullName +
            item.address;
        }

        // 直线距离（km 整数）
        list.value.forEach((it) => {
          it.distancekm = calcDistanceKm(
            { lat: innerCurrentLocation.lat, lng: innerCurrentLocation.lng },
            { lat: it.latitude, lng: it.longitude }
          );
          if (!it.latitude || !it.longitude) {
            it.showdistancekm = false;
          } else {
            it.showdistancekm = true;
          }
          console.log("it", it);
          console.log("it.distancekm", it.distancekm);
        });

        // 按距离升序排序（最小的排前面）
        list.value.sort((a, b) => {
          const da = Number(a?.distancekm ?? Infinity);
          const db = Number(b?.distancekm ?? Infinity);
          return da - db;
        });
      }
    }
  }, 1000);
  return { getStoreList };
};
const extractCity = (address) => {
  if (!address) return "";
  const m = String(address).match(/([^省市区县]+市)/);
  return m ? m[1] : "";
};
export default {
  name: "MapStorePanel",
  props: {
    dealers: { type: Array, default: () => [] },
    keyword: { type: String, default: "" },
    city: { type: String, default: "ALL" },
    activeKey: { type: [String, Number], default: null },
    currentLocation: { type: Object, default: () => {} },
    locationPermission: { type: Boolean, default: false },
  },
  emits: ["update:keyword", "update:city", "select", "navigate"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const panelHeight = ref(0);
    const minHeight = ref(0);
    const maxHeight = ref(0);

    /** 预留空状态高度（早期用于下滑收起，目前仅作为配置保留，可按需再用） */
    const emptyStateHeightPx = ref(0);

    const initHeights = () => {
      const vh = window.innerHeight || 667;
      maxHeight.value = vh * 0.55;
      const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize || "16"
      );
      minHeight.value = rootFontSize * 1.05;
      // 空状态总高度预估：拖拽条+标题+筛选行+文案区域
      emptyStateHeightPx.value = Math.min(vh * 0.2, rootFontSize * 13);
      // 默认进入页面：保持收起状态
      panelHeight.value = maxHeight.value;
    };

    if (typeof window !== "undefined") {
      initHeights();
      window.addEventListener("resize", initHeights);
    }

    const dragging = ref(false);
    let startY = 0;
    let startHeight = 0;

    const onTouchStart = (event) => {
      if (!maxHeight.value || !minHeight.value) initHeights();
      const touch = event.touches[0];
      dragging.value = true;
      startY = touch.clientY;
      startHeight = panelHeight.value;
    };

    // 当前最大高度：不再因空数据收缩，始终使用 maxHeight
    const effectiveMaxHeight = () => maxHeight.value;

    const onTouchMove = (event) => {
      if (!dragging.value) return;
      const touch = event.touches[0];
      const delta = startY - touch.clientY; // 向上拖动为正
      const cap = effectiveMaxHeight();
      let next = startHeight + delta;
      if (next < minHeight.value) next = minHeight.value;
      if (next > cap) next = cap;
      panelHeight.value = next;
    };

    const onTouchEnd = () => {
      if (!dragging.value) return;
      dragging.value = false;
      const cap = effectiveMaxHeight();
      const mid = (minHeight.value + cap) / 2;
      panelHeight.value = panelHeight.value > mid ? cap : minHeight.value;
    };

    const onHandleTap = () => {
      if (!maxHeight.value || !minHeight.value) initHeights();
      const cap = effectiveMaxHeight();
      const mid = (minHeight.value + cap) / 2;
      // 无数据时仅在「收起」与空状态高度间切换；有数据时仍为展开/收起
      panelHeight.value = panelHeight.value > mid ? minHeight.value : cap;
    };

    const innerKeyword = ref(props.keyword);
    const innerCity = ref(props.city || CHINA_DEFAULT_PROVINCE_ID);
    const innerCurrentLocation = reactive({ ...props.currentLocation });
    const innerLocationPermission = ref(props.locationPermission);
    const cityOptions = ref(CHINA_ALL_PROVINCE);

    const list = ref([]); // 底部显示的门店信息
    const { getStoreList } = useCitySourceEffect(
      innerCity,
      innerKeyword,
      innerCurrentLocation,
      list
    );

    watch(
      () => props.locationPermission,
      (v) => {
        innerLocationPermission.value = v;
      }
    );

    watch(
      locale,
      (v) => {
        cityOptions.value = getAllProvinceList();
      },
      { immediate: true }
    );

    watch(
      () => [props.currentLocation?.lat, props.currentLocation?.lng],
      ([lat, lng]) => {
        innerCurrentLocation.lat = lat;
        innerCurrentLocation.lng = lng;
        getStoreList();
      },
      { immediate: true }
    );

    watch(
      () => props.keyword,
      (v) => {
        innerKeyword.value = v;
        getStoreList();
      }
    );
    watch(
      () => props.city,
      (v) => {
        innerCity.value = v || CHINA_DEFAULT_PROVINCE_ID;
        getStoreList();
      }
    );

    // 优先使用父组件已拉取好的门店数据，避免子组件监听时机导致列表为空
    watch(
      () => props.dealers,
      (v) => {
        console.log("props.dealers", v);
        list.value = Array.isArray(v) ? v : [];
      },
      { immediate: true, deep: true }
    );

    //     watch(currentTab, (newValue, oldValue) => {
    //   // 依赖发生变化的时候 会进行更新
    //   console.log(`Count changed from ${oldValue} to ${newValue}`);

    //   if (newValue != oldValue) {
    //     getSpuFunc(newValue);
    //   }
    // });

    /**
     * 门店数据源：有接口数据用接口，否则用假数据（含电话、地址、公里数）
     */
    // const sourceDealers = computed(() => {
    //   const real = props.dealers || [];
    //   return Array.isArray(real) && real.length > 0 ? real : FAKE_DEALERS;
    // });

    /** 无门店城市，仅用于下拉选项，选中后 list 为空，可测空状态页 */
    const TEST_CITIES_NO_DEALERS = [
      // { text: "乌鲁木齐市", value: "乌鲁木齐市" },
      // { text: "喀什市", value: "喀什市" },
    ];

    const showCityPopup = ref(false);
    const citySearchKeyword = ref("");
    const currentCityLabel = computed(() => {
      const opts = cityOptions.value || [];
      const o = opts.find((x) => x.value === innerCity.value);
      return o ? o.text : t("app.home.map_store_all_provinces");
    });
    const hasCitySearchKeyword = computed(
      () => (citySearchKeyword.value || "").trim().length > 0
    );
    /** 城市弹层内根据输入模糊匹配的选项 */
    const filteredCityOptions = computed(() => {
      const opts = cityOptions.value || [];
      const kw = (citySearchKeyword.value || "").trim().toLowerCase();
      if (!kw) return opts;
      return opts.filter((o) => (o.text || "").toLowerCase().includes(kw));
    });

    const openCityPopup = () => {
      showCityPopup.value = true;
    };
    const onCityPopupOpen = () => {
      citySearchKeyword.value = "";
    };
    const selectCityMobile = (opt) => {
      innerCity.value = opt.value;
      emit("update:city", innerCity.value || CHINA_DEFAULT_PROVINCE_ID);
      showCityPopup.value = false;
      // 用户手动切换省份：自动展开
      if (!maxHeight.value || !minHeight.value) initHeights();
      panelHeight.value = maxHeight.value;
      getStoreList();
    };

    // const list = computed(() => {
    //   const kw = (props.keyword || "").trim().toLowerCase();
    //   const city = props.city && props.city !== "ALL" ? props.city : "";
    //   return (sourceDealers.value || []).filter((d) => {
    //     const inCity = city
    //       ? (d.city || d.cityName || extractCity(d.address)) === city
    //       : true;
    //     const hay = `${d.fullName || ""} ${d.id || ""} ${
    //       d.code || ""
    //     }`.toLowerCase();
    //     const inKw = kw ? hay.includes(kw) : true;
    //     return inCity && inKw;
    //   });
    // });

    const emitKeyword = () => {
      // 用户主动输入搜索：自动展开
      if (!maxHeight.value || !minHeight.value) initHeights();
      panelHeight.value = maxHeight.value;
      emit("update:keyword", innerKeyword.value);
    };
    /** clear 与 v-model.trim 组合时偶发不置空，这里显式同步到父组件 */
    const onClearKeyword = () => {
      innerKeyword.value = "";
      emit("update:keyword", "");
    };
    const emitCity = () => emit("update:city", innerCity.value || "ALL");

    const onSelect = (item, idx) => {
      emit("select", { item, idx, key: item.id ?? idx });
      // 用户主动点击门店：面板收起（给地图留更多空间）
      if (!maxHeight.value || !minHeight.value) initHeights();
      panelHeight.value = minHeight.value;
    };

    const onNavigate = (item, idx) => {
      emit("navigate", { item, idx, key: item.id ?? idx });
    };
    // 导航按钮点击时，同时触发与列表点击一致的选中联动效果
    const onNavigateWithSelect = (item, idx) => {
      onSelect(item, idx);
      onNavigate(item, idx);
    };

    const formatDistance = (item) => {
      const v = item.distanceKm ?? item.distance ?? item.kmDistance;
      if (v === 0) return "0km";
      if (!v) return "";
      const n = Number(v);
      if (Number.isFinite(n)) return `${n.toFixed(n >= 10 ? 0 : 1)}km`;
      return String(v);
    };

    const formatAuthRange = (item) => {
      const start =
        item.authorizationStartDate ||
        item.authorizeStartDate ||
        item.authStartDate ||
        item.startDate;
      const end =
        item.authorizationEndDate ||
        item.authorizeEndDate ||
        item.authEndDate ||
        item.endDate;
      if (!start && !end) return "";
      if (start && end) return `${start}至${end}`;
      return start ? `${start}起` : `至${end}`;
    };

    return {
      THEME,
      panelHeight,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onHandleTap,
      innerKeyword,
      onClearKeyword,
      innerCity,
      cityOptions,
      showCityPopup,
      citySearchKeyword,
      currentCityLabel,
      hasCitySearchKeyword,
      filteredCityOptions,
      openCityPopup,
      onCityPopupOpen,
      selectCityMobile,
      list,
      onSelect,
      onNavigate,
      onNavigateWithSelect,
      emitKeyword,
      emitCity,
      formatDistance,
      formatAuthRange,
      iconMapDown,
      iconMapUp,
      iconPhone,
      iconNav,
      iconLoc,
      iconSearch,
      innerLocationPermission,
      t,
    };
  },
};
</script>

<style lang="scss" scoped>
$mobile-aspect-ratio: 9 / 19.5;
.panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0.49rem;
  margin: 0 auto;
  /* 屏幕宽度小于 768px 用 100vw，其余用原逻辑 */
  max-width: 100vw;
  @media (min-width: 768px) {
    max-width: min(calc(100vh * #{$mobile-aspect-ratio}), 100vw);
  }
  background: #fff;

  border-top-left-radius: 0.16rem;
  border-top-right-radius: 0.16rem;
  box-shadow: 0 -0.06rem 0.18rem rgba(0, 0, 0, 0.08);
  z-index: 1950;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: height 0.5s ease;

  &__cityArrow {
    // flex-shrink: 0;
    // display: block;
    width: 0.11rem;
    height: 0.06rem;
    margin-left: 0.24rem;
    opacity: 0.6;
    // object-fit: contain;
  }

  &__handle {
    padding: 0.08rem 0rem 0.12rem 0rem;
    // display: flex;
    // flex-direction: column;
    // justify-content: center;
    // align-items: center;
    // background: orangered;
    &__bar {
      width: 0.36rem;
      height: 0.04rem;
      border-radius: 0.04rem;
      background: #d8d8d8;
      margin: 0 auto;
    }

    &__title {
      // background: orangered;
      margin-top: 0.12rem;
      margin-left: 0.15rem;
      text-align: left;
      // padding: 0rem 0.16rem 0.1rem;
      font-size: 0.14rem;
      font-weight: 700;
      color: #383838;
    }
  }

  &__filters {
    padding: 0 0.16rem 0.1rem;
    display: flex;
    gap: 0.1rem;
    align-items: center;

    /* 城市选择：设计稿 - 圆角框 + 浅灰边框 + 白底 + 右侧下拉箭头，与搜索框同高 */
    &__city {
      // min-width: 1.4rem;
      display: flex;
      align-items: center;
      // height: 0.36rem;
      height: 0.36rem;
      padding: 0 0.12rem;
      // border: 1px solid #e0e0e0;
      border: 0.01rem solid #cccccc;
      border-radius: 0.04rem;
      background: #fff;
      // box-sizing: border-box;
      font-size: 0.14rem;

      color: #383838;
    }
    &__cityTrigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      height: 0.36rem;
      padding: 0 0.1rem 0 0.12rem;
      font-size: 0.14rem;
      line-height: 1;
      color: #333;
      box-sizing: border-box;
    }
    &__cityTriggerText {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      min-width: 0;
      text-align: left;
    }

    &__search {
      flex: 1;
      border: 0.01rem solid #cccccc;
      border-radius: 0.08rem;
      padding: 0 0.08rem;
    }
  }

  &__searchIcon {
    display: block;
    width: 0.18rem;
    height: 0.18rem;
    object-fit: contain;
  }
  &__searchIconWrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  &__list {
    flex: 1;
    min-height: 0;
    overflow-y: scroll;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
    overscroll-behavior-y: contain;
  }

  /* 无数据空状态：与设计稿一致，文案两行 + 配图 */
  &__empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    // align-items: center;
    justify-content: flex-start;
    padding: 0.08rem 0.24rem 0.2rem;
    overflow: hidden;
  }
  &__empty__img {
    width: 100%;
    max-width: 2.8rem;
    max-height: 1.6rem;
    object-fit: contain;
    margin-bottom: 0.12rem;
  }
  &__empty__line1,
  &__empty__line2 {
    margin: 0;
    text-align: left;
    font-size: 0.14rem;
    color: #383838;
    line-height: 1.5;
  }
  &__empty__line2 {
    margin-top: 0.04rem;
    color: #707070;
    font-size: 0.12rem;
  }
}

.store {
  position: relative;
  // border: none;
  // border-radius: 0.1rem;
  padding: 0.16rem 0.16rem 0.16rem 0.16rem;
  background: #fff;
  // margin-bottom: 0.12rem;
  // border-bottom: 0.01rem solid #EBEBE;
  border-bottom: 1px solid #ebebeb;
  box-shadow: 0 0.02rem 0.12rem rgba(0, 0, 0, 0.06);
  text-align: left;

  &--active {
    background: rgba(0, 147, 67, 0.12);
    box-shadow: 0 0.02rem 0.14rem rgba(0, 0, 0, 0.08);
  }

  &--active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0.05rem;
    background: #009343;
    // border-top-left-radius: 0.1rem;
    // border-bottom-left-radius: 0.1rem;
  }

  &--active &__name {
    color: #009343;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 0.1rem;
  }
  &__name {
    flex: 1;
    text-align: left;
    font-size: 0.14rem;
    font-weight: 700;
    color: #383838;
    line-height: 1.2;
  }
  &__right {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.04rem;
    flex-shrink: 0;
    color: #8a8a8a;
    font-size: 0.13rem;
  }
  &__navicon {
    width: 0.1rem;
    height: 0.1rem;
    opacity: 0.85;
  }
  &__distance {
    color: #707070;
    font-size: 0.12rem;
  }
  &__badge {
    display: inline-block;
    margin-top: 0.08rem;
    padding: 0.04rem 0.12rem;
    border-radius: 0.12rem;
    border: 1px solid #009343;
    color: #009343;
    font-size: 0.1rem;
    background: rgba(0, 147, 67, 0.06);
  }

  &__main {
    margin-top: 0.08rem;
    display: flex;
    gap: 0.12rem;
    align-items: flex-end;
  }

  &__rows {
    flex: 1;
    min-width: 0;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 0.08rem;
    margin-top: 0.08rem;
    color: #555;
    font-size: 0.13rem;
    &:first-child {
      margin-top: 0rem;
    }
    &__imgicon {
      width: 0.12rem;
      height: 0.12rem;
      margin-top: 0.02rem;
      flex: 0 0 auto;
      opacity: 0.85;
    }
    &__location {
      width: 0.1rem;
      height: 0.12rem;
      margin-top: 0.02rem;
      flex: 0 0 auto;
      opacity: 0.85;
    }
    &__text {
      flex: 1;
      text-align: left;
      line-height: 1.25;
      word-break: break-all;
      font-size: 0.12rem;
      color: #383838;
    }
  }

  &__navbtn {
    flex: 0 0 auto;
    // height: 0.34rem;
    padding: 0.06rem 0.16rem;
    border-radius: 0.2rem;
    border: 0.01rem solid #cccccc;
    background: #fff;
    color: #707070;
    font-size: 0.13rem;
    // line-height: 0.34rem;
  }

  &--active &__navbtn {
    border-color: #009343;
    color: #009343;
    background: rgba(0, 147, 67, 0.06);
  }
}

/* 城市选择弹层 */
.panel__cityPopup {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.panel__cityPopupTitle {
  flex-shrink: 0;
  padding: 0.16rem;
  font-size: 0.16rem;
  font-weight: 600;
  color: #323233;
  // border-bottom: 1px solid #f2f2f2;
}
/* 城市搜索框：与主搜索框一致，并带右侧下拉箭头（保持原有样式） */
.panel__citySearchWrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 0.08rem 0 0;
  margin: 0 0.1rem 0.08rem;
  border: 1px solid #e7e7e7;
  border-radius: 0.08rem;
  background: #fff;
  box-sizing: border-box;
}
.panel__citySearch {
  flex: 1;
  min-width: 0;
  height: 0.36rem;
  border-top-left-radius: 0.08rem;
  border-bottom-left-radius: 0.08rem;
}
::v-deep(.panel__citySearch .van-cell) {
  padding: 0.02rem 0 0.02rem 0.08rem;
  border: none;
  border-radius: 0;
  box-shadow: none;
}
::v-deep(.panel__citySearch .van-field__control) {
  font-size: 0.14rem;
  line-height: 1.2;
}
.panel__citySearchClear {
  flex-shrink: 0;
  margin-left: 0.04rem;
  font-size: 0.16rem;
  color: #c8c9cc;
  padding: 0.04rem;
}
.panel__cityPopupList {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.panel__cityOption {
  padding: 0.14rem 0.16rem;
  font-size: 0.14rem;
  color: #323233;
  border-bottom: 1px solid #f2f2f2;
  text-align: left;
}
.panel__cityOption--active {
  color: #009343;
  font-weight: 600;
  background: rgba(0, 147, 67, 0.08);
}

.panel__filters__search {
  height: 0.36rem;
  box-sizing: content-box;
  // height: 100px;
}

/* 搜索框：让左侧图标与占位文案垂直对齐 */
::v-deep(.panel__filters__search .van-field__body) {
  align-items: center;
}
::v-deep(.panel__filters__search .van-field__left-icon) {
  display: flex;
  align-items: center;
  height: 100%;
}
::v-deep(.panel__filters__search .van-field__control) {
  line-height: 1.2;
  padding: 0;
}
::v-deep(.van-cell__value) {
  display: flex;
  align-items: center;
  justify-content: left;
}

::v-deep(.van-field__control) {
  font-size: 0.14rem;
}
</style>

